import {
  Animations,
  Button,
  ModalController,
  Spinners,
  useForm,
  useToast,
  Validators,
} from '@keymax-dev/smartepi-ui';
import { motion, useAnimation } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import PageTitle from '../../../../components/page-title';
import useRest from '../../../../hooks/use-rest';
import { User, ErrorResponse } from '../../../../domain/models';

import { EndPoints } from '../../../../utils/constants';
import { getErrorMessage } from '../../../../utils/error-messages';
import { EmployeeChangePasswordModalElement } from './style';

enum Content {
  Form,
  Loading,
}

interface EmployeeChangePasswordModalProps {
  controller?: ModalController;
  user?: User.Data;
}

const { usePut } = useRest();

const Strings = {
  Buttons: {
    Add: 'Confirmar',
  },
  Inputs: {
    NewPassword: 'Nova Senha',
  },
  Messages: {
    RegisterLoading: 'Atualizando senha...',
    RegisterSuccess: 'Senha atualizada com successo!',
    UnknownError:
      'Ocorreu um erro inesperado. Por favor, verifique sua conexão com a internet.',
    InvalidForm: 'A senha fornecida é inválida!',
  },
  Title: 'Atualizar Senha',
};

export default function EmployeeChangePasswordModal({
  controller,
  user,
}: EmployeeChangePasswordModalProps): JSX.Element {
  const [data, put] = usePut({ loading: false });
  const errorToast = useToast(<span />, { color: 'danger' });
  const successToast = useToast(
    <span>{Strings.Messages.RegisterSuccess}</span>,
    { color: 'success' }
  );

  const [form, getErrors, getValues] = useForm(
    [
      {
        key: 'password',
        validators: [new Validators.Required()],
      },
    ],
    'passwordChange'
  );

  const contentAnimation = useAnimation();
  const [content, _setContent] = useState<Content>(Content.Form);
  const setContent = (value: Content): void => {
    contentAnimation
      .start(Animations.FadeOut)
      .then(() => Promise.resolve(_setContent(value)))
      .then(() => contentAnimation.start(Animations.FadeIn));
  };

  const requestSuccessHandler = (data: User.Data): void => {
    setTimeout(() => successToast.open());
    if (controller) controller.close('success');
  };

  const requestErrorHandler = (error: ErrorResponse): void => {
    errorToast.setContent(getErrorMessage(error));
    setTimeout(() => errorToast.open());
  };

  const confirmHandler = (): void => {
    if (!getErrors()) {
      put(EndPoints.User(user?._id), getValues());
    } else {
      errorToast.setContent(Strings.Messages.InvalidForm);
      errorToast.open();
    }
  };

  useEffect(() => {
    if (data.loading) {
      setContent(Content.Loading);
    } else {
      setContent(Content.Form);
      if (data.payload) requestSuccessHandler(data.payload.data);
      else if (data.error) requestErrorHandler(data.error);
    }
  }, [data]);

  return (
    <EmployeeChangePasswordModalElement>
      <PageTitle>{Strings.Title}</PageTitle>

      {content === Content.Form && (
        <motion.div className="__form-container">
          <div className="__form-container">
            <form.password placeholder={Strings.Inputs.NewPassword} />
          </div>
          <Button
            text={Strings.Buttons.Add}
            color="success"
            onClick={confirmHandler}
          />
        </motion.div>
      )}
      {content === Content.Loading && (
        <motion.div
          className="__form-container"
          animate={contentAnimation}
          initial={{ opacity: 0 }}
        >
          <Spinners.circles width="300px" height="300px" />
          <span>{Strings.Messages.RegisterLoading}</span>
        </motion.div>
      )}
    </EmployeeChangePasswordModalElement>
  );
}
